// AddReceiptDialog - Modal form to add a new receipt
// Includes real file upload to Azure Blob Storage with image preview

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  PictureAsPdf as PdfIcon,
  Fullscreen as FullscreenIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { receiptsAPI } from '../../services/api';
import type { Receipt } from '../../types';
import dayjs from 'dayjs';

interface AddReceiptDialogProps {
  open: boolean;
  submissionId: string;
  programCategories: string[];
  onClose: () => void;
  onReceiptAdded: (receipt: Receipt) => void;
}

const AddReceiptDialog: React.FC<AddReceiptDialogProps> = ({
  open,
  submissionId,
  programCategories,
  onClose,
  onReceiptAdded,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form state
  const { showSuccess, showError } = useToast();
  const [vendor, setVendor] = useState('');
  const [receiptDate, setReceiptDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);

  // UI state
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewExpanded, setPreviewExpanded] = useState(!isMobile);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);

  // Update preview expansion based on screen size
  useEffect(() => {
    setPreviewExpanded(!isMobile);
  }, [isMobile]);

  // ========================================
  // FILE PREVIEW HANDLING
  // ========================================

  const isImage = (file: File) => {
    return file.type.startsWith('image/');
  };

  const isPdf = (file: File) => {
    return file.type === 'application/pdf';
  };

  useEffect(() => {
    // Clean up previous preview URL
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }

    // Create new preview URL for images
    if (file && isImage(file)) {
      const objectUrl = URL.createObjectURL(file);
      setFilePreviewUrl(objectUrl);
    } else {
      setFilePreviewUrl(null);
    }

    // Cleanup on unmount
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [file]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      // Auto-expand preview when file is selected
      setPreviewExpanded(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!vendor.trim() || !category || !amount || !file) {
      setError('Please fill in all required fields and select a file');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Upload file to Azure Blob Storage
      setUploading(true);
      const uploadResult = await receiptsAPI.uploadFile(submissionId, file);
      setUploading(false);

      // Create receipt with metadata
      const newReceipt = await receiptsAPI.addReceipt(submissionId, {
        vendor: vendor.trim(),
        receiptDate,
        expenseCategory: category,
        amount: amountNum,
        notes: notes.trim() || undefined,
        blobName: uploadResult.blobName,
        originalFileName: uploadResult.originalFileName,
        fileSize: uploadResult.fileSize,
        mimeType: uploadResult.mimeType,
      });

      // Success! Reset form and notify parent
      showSuccess(`Receipt added: ${vendor} - $${amountNum.toFixed(2)}`);
      resetForm();
      onReceiptAdded(newReceipt);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add receipt');
      console.error('Error adding receipt:', err);
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setVendor('');
    setReceiptDate(dayjs().format('YYYY-MM-DD'));
    setCategory('');
    setAmount('');
    setNotes('');
    setFile(null);
    setFilePreviewUrl(null);
    setError(null);
    setPreviewExpanded(!isMobile);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  // ========================================
  // RENDER PREVIEW SECTION
  // ========================================

  const renderPreview = () => {
    if (!file) return null;

    return (
      <Box sx={{ mb: 2 }}>
        {/* Preview Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Receipt Preview
          </Typography>
          <Box>
            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setPreviewExpanded(!previewExpanded)}
              >
                {previewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Preview Content */}
        <Collapse in={previewExpanded}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.100',
            }}
          >
            {/* Image Preview */}
            {isImage(file) && filePreviewUrl && (
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={filePreviewUrl}
                  alt="Receipt preview"
                  sx={{
                    width: '100%',
                    maxHeight: 400,
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.8)',
                    },
                  }}
                  onClick={() => setFullscreenPreview(true)}
                >
                  <FullscreenIcon />
                </IconButton>
              </Box>
            )}

            {/* PDF Preview */}
            {isPdf(file) && (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <PdfIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  PDF files cannot be previewed during upload
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You'll be able to view it after adding the receipt
                </Typography>
              </Box>
            )}

            {/* Other File Types */}
            {!isImage(file) && !isPdf(file) && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preview not available for this file type
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Receipt</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* File Upload */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: file ? 'success.main' : 'grey.300',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: file ? 'success.50' : 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  
                  {file ? (
                    <>
                      <CheckIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="body1" color="success.main">
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to change file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Click to upload receipt
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Images or PDF only
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Preview Section */}
              {file && (
                <Grid item xs={12}>
                  {renderPreview()}
                </Grid>
              )}

              {/* Vendor */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vendor"
                  placeholder="e.g., Staples, Best Buy"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  required
                />
              </Grid>

              {/* Receipt Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Receipt Date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Amount */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                  }}
                  inputProps={{
                    step: '0.01',
                    min: '0.01',
                  }}
                  required
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Expense Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  {programCategories.length > 0 ? (
                    programCategories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No categories available
                    </MenuItem>
                  )}
                </TextField>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes (optional)"
                  placeholder="Add any additional details..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>

              {/* Error Display */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || uploading}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {uploading ? 'Uploading...' : submitting ? 'Adding...' : 'Add Receipt'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Fullscreen Preview Dialog */}
      {file && isImage(file) && filePreviewUrl && (
        <Dialog
          open={fullscreenPreview}
          onClose={() => setFullscreenPreview(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Receipt Preview</Typography>
              <IconButton onClick={() => setFullscreenPreview(false)}>
                <ExpandLessIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'grey.900',
                minHeight: '70vh',
              }}
            >
              <Box
                component="img"
                src={filePreviewUrl}
                alt="Receipt preview fullscreen"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFullscreenPreview(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AddReceiptDialog;